import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
import {
  ArrowRight, RefreshCw, CheckCircle, XCircle, Activity, Target, AlertTriangle,
  TrendingUp, TrendingDown, BarChart2, Zap, Shield, Brain, Minus, RotateCcw,
  BookOpen, ChevronDown, ChevronRight, Wifi, WifiOff, Loader, Search, X,
} from 'lucide-react';
import {
  GLOBAL_CONTEXTS, FII_CONTEXTS, EXPIRY_CONTEXTS, TIME_SLOTS,
  NIFTY_GAPS, OPEN_BEHAVIORS, SETUPS, VIX_OPTIONS, INSTRUMENTS,
  GRADE_COLOR, computeScenario, classifyVix, resolveInstrumentLean,
} from '../utils/playbookLogic';
import { usePlaybookLive } from '../utils/usePlaybookLive';

const LEARN_NUDGES = [
  { level: 16, color: '#f59e0b', label: 'Pre-Trade Playbook',          sub: 'Full checklist before entering any trade' },
  { level: 14, color: '#6c63ff', label: 'Global Cues & Market Context', sub: 'SGX Nifty, US futures and FII flow' },
  { level: 15, color: '#4ecca3', label: 'Gap Analysis & Opening Play',  sub: 'How to play gap-up and gap-down opens' },
  { level: 7,  color: '#f97316', label: 'The Decision Framework',       sub: 'Grade your setup before placing an order' },
];

// ─── Node definitions ─────────────────────────────────────────────────────────
const NODES = [
  { id: 0, label: 'Global Context', hint: 'Check SGX Nifty, US futures, Asia markets first',              icon: TrendingUp, options: GLOBAL_CONTEXTS, color: '#6c63ff',
    quickLink: { label: 'Open Global Cues', path: '/global-cues' } },
  { id: 1, label: 'FII Stance',     hint: 'Check yesterday\'s FII net flow — published by NSE nightly',  icon: BarChart2,  options: FII_CONTEXTS,     color: '#4ecca3',
    quickLink: { label: 'Check FII Data', path: '/global-cues' } },
  { id: 2, label: 'Expiry Day',     hint: 'Tuesday = NSE expiry (Nifty + BankNifty) — premium rules change', icon: Activity,   options: EXPIRY_CONTEXTS,  color: '#f97316' },
  { id: 3, label: 'Time of Day',    hint: 'When you plan to trade changes which setups are valid',         icon: Zap,        options: TIME_SLOTS,        color: '#8b5cf6' },
  { id: 4, label: 'Gap',            hint: 'Compare today\'s open vs yesterday\'s close (gap %)',           icon: TrendingUp, options: NIFTY_GAPS,        color: '#f59e0b' },
  { id: 5, label: 'Open Behavior',  hint: 'Observe first 15 min after 9:15 AM — do not guess early',      icon: RotateCcw,  options: OPEN_BEHAVIORS,    color: '#ef4444' },
  { id: 6, label: 'Setup Type',     hint: 'Open scanner and charts — only select if clearly visible',      icon: Target,     options: SETUPS,             color: '#ce93d8',
    quickLinks: [{ label: 'Scanners', path: '/analytics' }, { label: 'Charts', path: '/markets' }] },
  { id: 7, label: 'India VIX',      hint: 'Check India VIX on NSE or your broker — reflects market fear level', icon: Activity,  options: VIX_OPTIONS,        color: '#f97316',
    quickLink: { label: 'Check VIX on NSE', path: '/global-cues' } },
  { id: 8, label: 'Instrument',     hint: 'Select the instrument you plan to use for this trade',          icon: Shield,     options: INSTRUMENTS,        color: '#818cf8' },
];

// ─── Option button ────────────────────────────────────────────────────────────
function OptionBtn({ opt, onPick }) {
  const Icon = opt.icon;
  return (
    <button
      onClick={() => onPick(opt)}
      className="w-full text-left rounded-xl px-3 py-2.5 flex items-start gap-2.5"
      style={{ background: `${opt.color}0d`, border: `1px solid ${opt.color}25` }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${opt.color}55`; e.currentTarget.style.background = `${opt.color}15`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = `${opt.color}25`; e.currentTarget.style.background = `${opt.color}0d`; }}
    >
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: `${opt.color}20` }}>
        <Icon size={13} color={opt.color} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-bold leading-snug" style={{ color: '#eceef5' }}>{opt.label}</p>
        <p className="text-[10px] mt-0.5 leading-snug" style={{ color: 'rgba(255,255,255,0.38)' }}>{opt.sub || opt.threshold || opt.when || opt.timeNote || opt.signal}</p>
        {opt.caution && (
          <p className="text-[9px] font-bold mt-1 px-1.5 py-0.5 rounded inline-block"
            style={{ background: `${opt.color}18`, color: opt.color }}>
            ⚠ {opt.caution}
          </p>
        )}
      </div>
      <ArrowRight size={12} color={`${opt.color}55`} style={{ flexShrink: 0, marginTop: 5 }} />
    </button>
  );
}

// ─── Verdict card ─────────────────────────────────────────────────────────────
function VerdictCard({ scenario, selections, navigate, onReset, vix, instrumentLean }) {
  const instrument = selections[8];
  const fii        = selections[1];
  const expiry     = selections[2];
  const timeSlot   = selections[3];
  const gradeColor = GRADE_COLOR[scenario.grade] || instrument.color;

  return (
    <div className="space-y-3">
      {/* Grade hero */}
      <div className="rounded-2xl overflow-hidden" style={{ border: `1.5px solid ${gradeColor}45` }}>
        {/* Rainbow rail from all 5 selections */}
        <div style={{ height: 3, background: `linear-gradient(90deg, ${selections.map(s => s.color).join(', ')})` }} />
        <div className="p-4" style={{ background: `linear-gradient(135deg, ${gradeColor}15 0%, #0d0e1c 100%)` }}>
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            {selections.map((s, i) => (
              <span key={i} className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: `${s.color}20`, color: s.color, border: `1px solid ${s.color}30` }}>
                {s.label}
              </span>
            ))}
          </div>
          {/* Grade + narrative */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <p className="text-[12px] leading-relaxed flex-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
              {scenario.narrative}
            </p>
            <div className="flex-shrink-0 text-right">
              <p className="text-[38px] font-black leading-none" style={{ color: gradeColor }}>{scenario.grade}</p>
              <p className="text-[9px] mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>session grade</p>
            </div>
          </div>
          {/* Sizing */}
          <div className="rounded-xl px-3 py-2" style={{ background: `${gradeColor}12`, border: `1px solid ${gradeColor}28` }}>
            <p className="text-[11px] font-bold" style={{ color: gradeColor }}>{scenario.sizing}</p>
          </div>
        </div>
      </div>

      {/* Contradictions */}
      {scenario.contradictions.length > 0 && (
        <div className="rounded-xl p-3 space-y-2" style={{ background: '#f9731610', border: '1px solid #f9731628' }}>
          <p className="text-[10px] font-black uppercase tracking-wider" style={{ color: '#f97316' }}>⚠ Conflicts detected</p>
          {scenario.contradictions.map((c, i) => (
            <div key={i} className="flex items-start gap-2">
              <AlertTriangle size={12} color="#f97316" style={{ flexShrink: 0, marginTop: 2 }} />
              <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>{c}</p>
            </div>
          ))}
        </div>
      )}

      {/* Expiry warning */}
      {scenario.expiryNote && expiry?.isExpiry && (
        <div className="rounded-xl p-3" style={{ background: '#f9731610', border: '1px solid #f9731630' }}>
          <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: '#f97316' }}>⚠ Expiry Day</p>
          <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>{scenario.expiryNote}</p>
        </div>
      )}

      {/* VIX warning — passive badge, shown only when VIX > 20 */}
      {(() => {
        if (!vix) return null;
        const vl = classifyVix(vix);
        if (vl.label === 'Normal' || vl.label === 'Elevated') return null; // only warn above 20
        const color = vl.label === 'High' ? '#f97316' : '#ef4444';
        return (
          <div className="rounded-xl p-3" style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
            <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color }}>
              ⚠ India VIX: {vix.toFixed(1)} — {vl.label}
            </p>
            <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>{vl.note}</p>
          </div>
        );
      })()}

      {/* Time of day note */}
      {scenario.timeNote && timeSlot && (
        <div className="rounded-xl p-3" style={{ background: `${timeSlot.color}10`, border: `1px solid ${timeSlot.color}28` }}>
          <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: timeSlot.color }}>{timeSlot.label}</p>
          <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>{scenario.timeNote}</p>
        </div>
      )}

      {/* FII note */}
      {scenario.fiiNote && fii && fii.bias !== 'neutral' && (
        <div className="rounded-xl p-3" style={{ background: `${fii.color}10`, border: `1px solid ${fii.color}28` }}>
          <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: fii.color }}>FII — {fii.label}</p>
          <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>{scenario.fiiNote}</p>
        </div>
      )}

      {/* Instrument lean — soft nudge, not a signal */}
      {instrumentLean && scenario.grade !== 'Sit Out' && (
        <div className="rounded-xl p-3" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.22)' }}>
          <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: '#818cf8' }}>
            Trade Type Lean
          </p>
          <p className="text-[12px] font-bold mb-1" style={{ color: '#c7d2fe' }}>{instrumentLean.label}</p>
          <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.45)' }}>{instrumentLean.note}</p>
        </div>
      )}

      {/* Entry plan */}
      {scenario.setupEntry && (
        <div className="rounded-xl p-3 space-y-1.5" style={{ background: `${instrument.color}0a`, border: `1px solid ${instrument.color}25` }}>
          <p className="text-[10px] font-black uppercase tracking-wider mb-2" style={{ color: instrument.color }}>Entry Plan</p>
          <p className="text-[11px]" style={{ color: '#eceef5' }}><span className="font-bold">Entry: </span>{scenario.setupEntry}</p>
          {scenario.setupSL     && <p className="text-[11px]" style={{ color: '#ef4444' }}><span className="font-bold">SL: </span>{scenario.setupSL}</p>}
          {scenario.setupTarget && <p className="text-[11px]" style={{ color: '#16C784' }}><span className="font-bold">Target: </span>{scenario.setupTarget}</p>}
        </div>
      )}

      {/* Action sequence */}
      <div className="rounded-xl p-3 space-y-2" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-[10px] font-black uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>Action sequence</p>
        {scenario.actions.map((action, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0 mt-0.5"
              style={{ background: `${instrument.color}22`, color: instrument.color }}>
              {i + 1}
            </span>
            <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>{action}</p>
          </div>
        ))}
      </div>

      {/* Rules */}
      <div className="rounded-xl p-3 space-y-1.5" style={{ background: '#16C78410', border: '1px solid #16C78425' }}>
        <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: '#16C784' }}>Rules</p>
        {scenario.rules.slice(0, 4).map((r, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle size={11} color="#16C784" style={{ flexShrink: 0, marginTop: 2 }} />
            <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>{r}</p>
          </div>
        ))}
      </div>

      {/* Avoid */}
      <div className="rounded-xl p-3 space-y-1.5" style={{ background: '#ef444410', border: '1px solid #ef444425' }}>
        <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: '#ef4444' }}>Avoid</p>
        {scenario.avoid.slice(0, 4).map((a, i) => (
          <div key={i} className="flex items-start gap-2">
            <XCircle size={11} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
            <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>{a}</p>
          </div>
        ))}
      </div>

      {/* CTA */}
      {scenario.grade !== 'Sit Out' && (
        <button
          onClick={() => navigate(scenario.calcPath)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[13px] font-bold"
          style={{ background: instrument.color, color: '#fff' }}
        >
          {instrument.id === 'equity' ? 'Open Risk Calculator' : 'Open Derivatives Desk'}
          <ArrowRight size={15} />
        </button>
      )}

      <button onClick={onReset} className="w-full text-center py-2 text-[11px]"
        style={{ color: 'rgba(255,255,255,0.25)' }}>
        ↺ New session
      </button>
    </div>
  );
}

// ─── Stock selector ───────────────────────────────────────────────────────────
function StockSelector({ stockList, selectedSymbol, onSelect, liveData, isLive }) {
  const [query, setQuery]     = useState('');
  const wrapperRef            = useRef(null);

  const ticker        = selectedSymbol?.replace('.NS', '') || null;
  const selectedStock = selectedSymbol ? stockList.find(s => s.symbol === selectedSymbol) : null;
  const showResults   = query.length > 0;

  const filtered = showResults
    ? stockList.filter(s =>
        s.symbol.replace('.NS','').toLowerCase().includes(query.toLowerCase()) ||
        (s.company_name || '').toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8)
    : [];

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setQuery('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (symbol) => { onSelect(symbol); setQuery(''); };
  const handleClear  = () => { onSelect(null); setQuery(''); };
  const stockCtx     = liveData?.stockCtx;

  return (
    <div ref={wrapperRef} className="mb-4" style={{ position: 'relative' }}>

      {/* Single card */}
      <div className="rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.09)' }}>

        {/* Heading row */}
        <div className="flex items-center justify-between px-4 pt-3.5 pb-3"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Analyse for
          </p>
          {selectedSymbol && (
            <button onClick={handleClear} className="flex items-center gap-1 text-[10px] font-semibold"
              style={{ color: 'rgba(255,255,255,0.25)' }}>
              <X size={9} /> Clear
            </button>
          )}
        </div>

        {/* Current selection — Nifty or stock */}
        <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-center w-8 h-8 rounded-xl flex-shrink-0"
            style={{ background: selectedSymbol ? 'rgba(22,199,132,0.15)' : 'rgba(108,99,255,0.15)' }}>
            {selectedSymbol
              ? <TrendingUp size={14} style={{ color: '#16C784' }} />
              : <BarChart2  size={14} style={{ color: '#a78bfa' }} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold leading-none" style={{ color: selectedSymbol ? '#16C784' : '#a78bfa' }}>
              {selectedSymbol ? ticker : 'Nifty 50'}
            </p>
            <p className="text-[10px] mt-1 leading-none" style={{ color: 'rgba(255,255,255,0.3)' }}>
              {selectedSymbol ? (selectedStock?.company_name || 'Stock') : 'Default — index level analysis'}
            </p>
          </div>
          {/* Live chips */}
          {isLive && selectedSymbol && stockCtx?.data_available && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {[
                `${stockCtx.gap_value >= 0 ? '+' : ''}${stockCtx.gap_value?.toFixed(2)}%`,
                stockCtx.open_behavior,
              ].map(label => (
                <span key={label} className="text-[9px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(22,199,132,0.1)', color: 'rgba(22,199,132,0.75)', border: '1px solid rgba(22,199,132,0.2)' }}>
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Search input — always visible */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Search size={12} style={{ color: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
            <input
              type="text"
              placeholder={selectedSymbol ? `Switch from ${ticker}…` : 'Search stock — RELIANCE, TATASTEEL, HDFC…'}
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="flex-1 bg-transparent text-[12px] outline-none"
              style={{ color: '#eceef5', minWidth: 0 }}
            />
            {query
              ? <button onClick={() => setQuery('')} style={{ flexShrink: 0 }}><X size={10} style={{ color: 'rgba(255,255,255,0.2)' }} /></button>
              : !selectedSymbol && <p className="text-[10px] flex-shrink-0" style={{ color: 'rgba(255,255,255,0.15)' }}>or keep Nifty</p>
            }
          </div>
        </div>

      </div>

      {/* Dropdown — outside card, never clipped */}
      {showResults && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          marginTop: 6, borderRadius: 16, overflow: 'hidden',
          background: '#13142a', border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.7)',
          zIndex: 999,
        }}>
          {filtered.length === 0 ? (
            <div className="px-4 py-3.5">
              <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {stockList.length === 0 ? 'Loading stocks…' : `No match for "${query}"`}
              </p>
            </div>
          ) : filtered.map((s, idx) => {
            const t = s.symbol.replace('.NS', '');
            const isActive = s.symbol === selectedSymbol;
            return (
              <button key={s.symbol} onMouseDown={() => handleSelect(s.symbol)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left"
                style={{
                  borderBottom: idx < filtered.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                  background: isActive ? 'rgba(22,199,132,0.08)' : 'transparent',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? 'rgba(22,199,132,0.08)' : 'transparent'; }}>
                <div>
                  <span className="text-[13px] font-bold" style={{ color: isActive ? '#16C784' : '#eceef5' }}>{t}</span>
                  <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.company_name}</p>
                </div>
                {s.sector && (
                  <span className="text-[9px] px-2 py-0.5 rounded-full ml-2 flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {s.sector}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function PlaybookMap({ colors, navigate }) {
  const [isLive,         setIsLive]         = useState(false);
  const [selections,     setSelections]     = useState([null, null, null, null, null, null, null, null, null]);
  const [editingIdx,     setEditingIdx]     = useState(null);
  const [learnOpen,      setLearnOpen]      = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null); // null = Nifty (market level)
  const [stockList,      setStockList]      = useState([]);

  // Fetch stock list once on mount
  useEffect(() => {
    axios.get(`${API_URL}/stocks`)
      .then(r => setStockList(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const { loading: liveLoading, error: liveError, selections: liveSel, liveData, load: loadLive } = usePlaybookLive(selectedSymbol);

  // In manual mode: when stock selected, auto-fill node 8 with stock instrument
  useEffect(() => {
    if (!isLive && selectedSymbol) {
      const ticker = selectedSymbol.replace('.NS', '');
      const equityBase = INSTRUMENTS.find(i => i.id === 'equity');
      if (equityBase) {
        setSelections(prev => {
          const next = [...prev];
          next[8] = { ...equityBase, label: `${ticker} — Equity`, sub: 'Stock selected — confirm instrument type' };
          return next;
        });
      }
    }
    if (!isLive && !selectedSymbol) {
      setSelections(prev => {
        const next = [...prev];
        if (next[8]?.label?.includes('—')) next[8] = null; // clear auto-filled stock instrument
        return next;
      });
    }
  }, [selectedSymbol, isLive]);

  // In live mode, merge live selections with any manual overrides (setup/instrument always manual)
  const activeSel = isLive && liveSel
    ? liveSel.map((v, i) => selections[i] !== null ? selections[i] : v)
    : selections;

  const completedCount = activeSel.filter(Boolean).length;
  const allDone        = completedCount === 9;
  // selections: [global(0), fii(1), expiry(2), timeSlot(3), gap(4), openBehavior(5), setup(6), vix(7), instrument(8)]
  const vixVal = activeSel[7]?.vix ?? null;
  const scenario = allDone
    ? computeScenario(activeSel[0], activeSel[4], activeSel[5], activeSel[6], activeSel[8], activeSel[1], vixVal, activeSel[2], activeSel[3])
    : null;

  const reset = () => { setSelections([null, null, null, null, null, null, null, null, null]); setEditingIdx(null); };

  const switchToLive = () => {
    setIsLive(true);
    setSelections([null, null, null, null, null, null, null, null, null]);
    setEditingIdx(null);
    loadLive();
  };

  const switchToManual = () => {
    setIsLive(false);
    setSelections([null, null, null, null, null, null, null, null, null]);
    setEditingIdx(null);
  };

  const handleSymbolChange = (symbol) => {
    setSelectedSymbol(symbol || null);
    setSelections([null, null, null, null, null, null, null, null, null]);
    setEditingIdx(null);
    if (isLive) loadLive();
  };

  const pick = (nodeIdx, option) => {
    setSelections(prev => {
      const next = [...prev];
      next[nodeIdx] = option;
      // In manual mode clear downstream; in live mode only clear setup/instrument downstream
      const clearFrom = isLive ? Math.max(nodeIdx + 1, 7) : nodeIdx + 1;
      for (let i = clearFrom; i < 9; i++) next[i] = null;
      return next;
    });
    setEditingIdx(null);
  };

  return (
    <div>

      {/* ── Header ── */}
      <div className="rounded-2xl p-4 mb-4"
        style={{ background: 'linear-gradient(135deg, #6c63ff12 0%, #0d0e1c 100%)', border: '1px solid rgba(108,99,255,0.2)' }}>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: '#6c63ff' }}>Pre-Trade Playbook</p>
          {completedCount > 0 && (
            <button onClick={reset}
              className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>
              <RefreshCw size={9} /> Reset
            </button>
          )}
        </div>
        <p className="text-[16px] font-black leading-tight mb-1" style={{ color: '#eceef5', letterSpacing: '-0.02em' }}>
          9 inputs. One verdict.
        </p>
        <p className="text-[11px] leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Select your current market conditions — get a grade and a step-by-step action plan.
        </p>

        {/* Progress rail */}
        <div className="flex items-center gap-1.5">
          {NODES.map((n, i) => (
            <div key={i} style={{
              flex: activeSel[i] ? '0 0 20px' : '0 0 8px',
              height: 4,
              borderRadius: 4,
              background: activeSel[i] ? activeSel[i].color : 'rgba(255,255,255,0.1)',
              transition: 'all 0.35s ease',
            }} />
          ))}
          {allDone && scenario && (
            <span className="text-[11px] font-black ml-1.5" style={{ color: GRADE_COLOR[scenario.grade] }}>
              → {scenario.grade}
            </span>
          )}
        </div>
      </div>

      {/* ── Learn the basics nudge (collapsible) ── */}
      <div className="rounded-2xl mb-5 overflow-hidden"
        style={{
          background: learnOpen ? 'rgba(255,255,255,0.03)' : 'linear-gradient(135deg, #6c63ff18 0%, #f59e0b0a 100%)',
          border: learnOpen ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(108,99,255,0.35)',
          transition: 'background 0.2s, border-color 0.2s',
        }}>
        <button
          onClick={() => setLearnOpen(o => !o)}
          className="w-full flex items-center justify-between px-4 py-3"
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" style={{ color: learnOpen ? 'rgba(255,255,255,0.4)' : '#6c63ff' }} />
            <p className="text-[11px] font-black uppercase tracking-widest"
              style={{ color: learnOpen ? 'rgba(255,255,255,0.4)' : '#a78bfa' }}>
              Study the playbook
            </p>
          </div>
          <ChevronDown
            className="w-3.5 h-3.5 transition-transform duration-200"
            style={{ color: learnOpen ? 'rgba(255,255,255,0.25)' : '#6c63ff99', transform: learnOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
          />
        </button>
        {learnOpen && (
          <div className="px-4 pb-4">
            <p className="text-[11px] mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Read these modules to understand how to plan your trades using the playbook.
            </p>
            <div className="space-y-1.5">
              {LEARN_NUDGES.map(({ level, color, label, sub }) => (
                <button
                  key={level}
                  onClick={() => navigate(`/learning?view=level&level=${level}&card=0`)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
                  style={{ background: `${color}0d`, border: `1px solid ${color}22` }}
                >
                  <div className="flex items-center gap-2.5">
                    <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                    <div>
                      <p className="text-[11px] font-semibold leading-snug" style={{ color }}>{label}</p>
                      <p className="text-[9px] leading-snug" style={{ color: 'rgba(255,255,255,0.35)' }}>{sub}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: `${color}50` }} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Manual / Live toggle ── */}
      <div className="rounded-2xl overflow-hidden mb-4"
        style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}>
        <div className="grid grid-cols-2">
          <button
            onClick={switchToManual}
            className="w-full flex items-center justify-between px-4 py-3 transition-all"
            style={{
              background: !isLive ? 'rgba(108,99,255,0.1)' : 'transparent',
              borderRight: '1px solid rgba(255,255,255,0.07)',
            }}>
            <div className="flex items-center gap-2">
              <WifiOff className="w-3.5 h-3.5" style={{ color: !isLive ? '#a78bfa' : 'rgba(255,255,255,0.25)' }} />
              <p className="text-[11px] font-black uppercase tracking-widest"
                style={{ color: !isLive ? '#a78bfa' : 'rgba(255,255,255,0.25)' }}>
                Manual
              </p>
            </div>
            {!isLive && <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(167,139,250,0.2)' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#a78bfa' }} />
            </div>}
          </button>

          <button
            onClick={switchToLive}
            disabled={liveLoading}
            className="w-full flex items-center justify-between px-4 py-3 transition-all"
            style={{
              background: isLive ? 'rgba(22,199,132,0.08)' : 'transparent',
              opacity: liveLoading ? 0.7 : 1,
            }}>
            <div className="flex items-center gap-2">
              {liveLoading
                ? <Loader className="w-3.5 h-3.5 animate-spin" style={{ color: '#16C784' }} />
                : <Wifi className="w-3.5 h-3.5" style={{ color: isLive ? '#16C784' : 'rgba(255,255,255,0.25)' }} />
              }
              <p className="text-[11px] font-black uppercase tracking-widest"
                style={{ color: isLive ? '#16C784' : 'rgba(255,255,255,0.25)' }}>
                {liveLoading ? 'Loading…' : 'Live'}
              </p>
            </div>
            {isLive && !liveLoading && <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(22,199,132,0.2)' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#16C784' }} />
            </div>}
          </button>
        </div>

        {/* Live status line */}
        {isLive && liveSel && !liveLoading && (
          <div className="px-4 py-2"
            style={{ borderTop: '1px solid rgba(22,199,132,0.12)', background: 'rgba(22,199,132,0.04)' }}>
            <p style={{ fontSize: 10, color: 'rgba(22,199,132,0.6)', margin: 0 }}>
              Data as of {liveData?.simDate} · Pick Instrument to complete
            </p>
          </div>
        )}
        {isLive && liveError && (
          <div className="px-4 py-2" style={{ borderTop: '1px solid rgba(239,68,68,0.15)' }}>
            <p style={{ fontSize: 10, color: '#ef4444' }}>{liveError}</p>
          </div>
        )}

      </div>

      {/* ── Analyse for — stock selector ── */}
      <StockSelector
        stockList={stockList}
        selectedSymbol={selectedSymbol}
        onSelect={handleSymbolChange}
        liveData={liveData}
        isLive={isLive}
      />

      {/* ── Waterfall nodes ── */}
      <div>
        {NODES.map((node, i) => {
          // In live mode nodes 0–6 are auto-filled — show as completed summary only
          const isLiveAutoFilled = isLive && liveSel && i < 8 && liveSel[i]; // 0–7 auto-filled; 8=Instrument always manual

          const sel       = activeSel[i];
          const completed = sel !== null;
          const isEditing = editingIdx === i;
          const prevSel   = i > 0 ? activeSel[i - 1] : null;
          const isActive  = !completed && (i === 0 || prevSel !== null);
          const isExpanded = (isActive || isEditing) && !isLiveAutoFilled;
          const locked    = !completed && !isActive;

          // In live mode auto-filled nodes: don't show options, just a compact pill
          if (isLiveAutoFilled && !isEditing) {
            // Data badge — shows the raw value that drove the pick
            const cues = liveData?.cues;
            const regime = liveData?.regime;
            let dataBadge = null;
            if (i === 0 && cues?.mood) {
              // Global: show mood label
              dataBadge = cues.mood.label;
            } else if (i === 1 && cues?.fii_dii?.fii?.net != null) {
              const net = cues.fii_dii.fii.net;
              const cr = Math.round(net / 10000000);
              dataBadge = cr === 0 ? 'Neutral' : `₹${cr > 0 ? '+' : ''}${cr} Cr`;
            } else if (i === 2) {
              const simDt = liveData?.simDate ? new Date(liveData.simDate + 'T00:00:00') : new Date();
              const simDay = simDt.getDay();
              const dayStr = simDt.toLocaleDateString('en-IN', { weekday: 'short' });
              dataBadge = simDay === 2 ? `${dayStr} · Expiry` : dayStr;
            } else if (i === 3) {
              dataBadge = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
            } else if (i === 4) {
              const stockCtx = liveData?.stockCtx;
              if (selectedSymbol && stockCtx?.data_available) {
                const gv = stockCtx.gap_value;
                dataBadge = `${selectedSymbol.replace('.NS', '')} ${gv >= 0 ? '+' : ''}${gv.toFixed(2)}%`;
              } else if (regime?.gap_value != null) {
                const gv = parseFloat(regime.gap_value);
                dataBadge = `${gv >= 0 ? '+' : ''}${gv.toFixed(2)}%`;
              }
            } else if (i === 5) {
              const stockCtx = liveData?.stockCtx;
              if (selectedSymbol && stockCtx?.data_available) {
                dataBadge = stockCtx.open_behavior;
              } else if (regime) {
                dataBadge = sel?.label || (regime.conflict ? 'Conflict' : regime.match ? 'Confirmed' : 'Neutral');
              }
            } else if (i === 6) {
              dataBadge = 'Auto-resolved';
            } else if (i === 7 && liveData?.vix != null) {
              dataBadge = `VIX ${liveData.vix.toFixed(1)}`;
            }

            const LiveSelIcon = sel.icon;
            return (
              <div key={node.id} className="flex mb-1.5">
                {/* Left track dot */}
                <div className="flex flex-col items-center" style={{ width: 52, flexShrink: 0 }}>
                  {i > 0 && <div style={{ width: 2, height: 22, background: `${sel.color}55` }} />}
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: `${sel.color}25`, border: `1.5px solid ${sel.color}55` }}>
                    <Wifi size={11} color={sel.color} />
                  </div>
                  <div style={{ width: 2, flex: 1, minHeight: 8, background: `${sel.color}40` }} />
                </div>
                {/* Right content */}
                <div className="flex-1 min-w-0 pb-1" style={{ paddingTop: 9 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-[13px] font-black" style={{ color: sel.color }}>{node.label}</p>
                    {dataBadge && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        {dataBadge}
                      </span>
                    )}
                    <button onClick={() => setEditingIdx(prev => prev === i ? null : i)}
                      className="text-[9px] px-1.5 py-0.5 rounded ml-auto flex-shrink-0"
                      style={{ color: 'rgba(255,255,255,0.25)' }}>override</button>
                  </div>
                  <div className="rounded-xl px-3 py-2.5 flex items-start gap-2.5 mr-1"
                    style={{ background: `${sel.color}10`, border: `1px solid ${sel.color}30` }}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${sel.color}20` }}>
                      <LiveSelIcon size={13} color={sel.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-bold leading-snug" style={{ color: sel.color }}>{sel.label}</p>
                      <p className="text-[10px] mt-0.5 leading-snug" style={{ color: 'rgba(255,255,255,0.38)' }}>
                        {sel.sub || sel.threshold || sel.when || sel.timeNote || sel.signal}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          const nodeColor   = completed ? sel.color : isActive ? node.color : 'rgba(255,255,255,0.12)';
          const topLineClr  = i > 0 && activeSel[i - 1] ? `${activeSel[i - 1].color}55` : 'rgba(255,255,255,0.06)';
          const botLineClr  = completed ? `${sel.color}55` : 'rgba(255,255,255,0.06)';
          const Icon        = node.icon;

          return (
            <div key={node.id} className="flex">

              {/* ── Left track ── */}
              <div className="flex flex-col items-center" style={{ width: 52, flexShrink: 0 }}>
                {/* Top connector */}
                {i > 0 && (
                  <div style={{
                    width: 2, height: 22,
                    background: topLineClr,
                    transition: 'background 0.5s ease',
                  }} />
                )}

                {/* Node circle */}
                <button
                  onClick={() => completed && setEditingIdx(prev => prev === i ? null : i)}
                  style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: completed
                      ? `linear-gradient(135deg, ${sel.color}, ${sel.color}99)`
                      : isActive
                      ? `${node.color}18`
                      : 'rgba(255,255,255,0.04)',
                    border: `2px solid ${completed ? sel.color : isActive ? `${node.color}55` : 'rgba(255,255,255,0.1)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    opacity: locked ? 0.3 : 1,
                    cursor: completed ? 'pointer' : 'default',
                    transition: 'all 0.3s ease',
                    boxShadow: isActive
                      ? `0 0 20px ${node.color}40`
                      : completed
                      ? `0 0 14px ${sel.color}35`
                      : 'none',
                  }}
                >
                  <Icon size={18} color={completed ? '#fff' : isActive ? node.color : 'rgba(255,255,255,0.2)'} />
                </button>

                {/* Bottom connector */}
                {i < NODES.length - 1 && (
                  <div style={{
                    width: 2, flex: 1, minHeight: 20,
                    background: botLineClr,
                    transition: 'background 0.5s ease',
                  }} />
                )}
              </div>

              {/* ── Right content ── */}
              <div className="flex-1 min-w-0 pb-1" style={{ opacity: locked ? 0.3 : 1, transition: 'opacity 0.3s' }}>
                {/* Node header */}
                <div style={{ paddingTop: 9, paddingBottom: isExpanded ? 10 : 5 }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[13px] font-black"
                      style={{ color: completed ? sel.color : isActive ? '#eceef5' : 'rgba(255,255,255,0.22)' }}>
                      {node.label}
                    </p>
                    {completed && !isEditing && (
                      <button
                        onClick={() => setEditingIdx(prev => prev === i ? null : i)}
                        className="text-[9px] px-1.5 py-0.5 rounded"
                        style={{ color: 'rgba(255,255,255,0.22)' }}>
                        edit
                      </button>
                    )}
                    {isEditing && (
                      <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ background: `${sel.color}20`, color: sel.color }}>
                        editing
                      </span>
                    )}
                    {isActive && (
                      <span className="text-[9px] font-black uppercase tracking-wider animate-pulse"
                        style={{ color: node.color }}>
                        ← select
                      </span>
                    )}
                  </div>
                  {isExpanded && (
                    <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                      {node.hint}
                    </p>
                  )}
                </div>

                {/* Selected card — stays visible after picking */}
                {completed && !isEditing && (() => {
                  const SelIcon = sel.icon;
                  return (
                    <div className="rounded-xl px-3 py-2.5 flex items-start gap-2.5 mb-3 pr-1"
                      style={{ background: `${sel.color}10`, border: `1px solid ${sel.color}30` }}>
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: `${sel.color}20` }}>
                        <SelIcon size={13} color={sel.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-bold leading-snug" style={{ color: sel.color }}>{sel.label}</p>
                        <p className="text-[10px] mt-0.5 leading-snug" style={{ color: 'rgba(255,255,255,0.38)' }}>
                          {sel.sub || sel.threshold || sel.when || sel.timeNote || sel.signal}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Options */}
                {isExpanded && (
                  <div className="space-y-1.5 pr-1 mb-3">
                    {node.options.map(opt => (
                      <OptionBtn key={opt.id} opt={opt} onPick={(o) => pick(i, o)} />
                    ))}
                    {/* Quick-action links from node config */}
                    {node.quickLink && (
                      <button onClick={() => navigate(node.quickLink.path)}
                        className="w-full flex items-center justify-center gap-1 py-2 rounded-xl text-[10px] font-bold"
                        style={{ background: `${node.color}10`, color: node.color, border: `1px solid ${node.color}20` }}>
                        {node.quickLink.label} <ArrowRight size={10} />
                      </button>
                    )}
                    {node.quickLinks && (
                      <div className="flex gap-1.5">
                        {node.quickLinks.map(ql => (
                          <button key={ql.path} onClick={() => navigate(ql.path)}
                            className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl text-[10px] font-bold"
                            style={{ background: `${node.color}10`, color: node.color, border: `1px solid ${node.color}20` }}>
                            {ql.label} <ArrowRight size={10} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Connector drip into verdict ── */}
      {allDone && scenario && (
        <>
          <div className="flex" style={{ height: 20 }}>
            <div className="flex flex-col items-center" style={{ width: 52 }}>
              <div style={{ width: 2, height: '100%', background: `${activeSel[8].color}55` }} />
            </div>
          </div>
          <VerdictCard
            scenario={scenario}
            selections={activeSel}
            navigate={navigate}
            onReset={reset}
            vix={vixVal}
            instrumentLean={resolveInstrumentLean(activeSel[0], activeSel[1], vixVal, activeSel[2], activeSel[6])}
          />
        </>
      )}

    </div>
  );
}
